import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { ClinanceDocument, DocumentType, DocumentFilters } from './types';

export function useDocuments(
  filters?: Partial<DocumentFilters> & { transactionId?: string | null },
) {
  const { clientId } = useCurrentClient();

  return useQuery({
    queryKey: ['documents', clientId, filters],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ClinanceDocument[]> => {
      if (!clientId) return [];
      let q = supabase.from('documents').select('*').eq('client_id', clientId);

      if (filters?.transactionId) q = q.eq('transaction_id', filters.transactionId);
      if (filters?.type && filters.type !== 'all') q = q.eq('document_type', filters.type);
      if (filters?.fromDate) q = q.gte('document_date', filters.fromDate);
      if (filters?.toDate) q = q.lte('document_date', filters.toDate);
      if (filters?.search && filters.search.trim().length > 0) {
        const term = filters.search.trim().replace(/%/g, '');
        q = q.or(`name.ilike.%${term}%,supplier_name.ilike.%${term}%,notes.ilike.%${term}%`);
      }

      const { data, error } = await q.order('created_at', { ascending: false }).limit(500);
      if (error) throw error;
      return (data ?? []) as ClinanceDocument[];
    },
  });
}

export interface UploadDocumentInput {
  file: File;
  name: string;
  documentType: DocumentType;
  supplierName?: string;
  documentDate?: string; // YYYY-MM-DD
  notes?: string;
  transactionId?: string | null;
}

export function useUploadDocument() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: UploadDocumentInput): Promise<ClinanceDocument> => {
      if (!clientId) throw new Error('Cliente não selecionado');
      if (!user?.id) throw new Error('Não autenticado');

      const sanitized = input.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${clientId}/${Date.now()}_${sanitized}`;

      const { error: uploadErr } = await supabase.storage
        .from('documents')
        .upload(path, input.file, { contentType: input.file.type, upsert: false });
      if (uploadErr) throw uploadErr;

      const { data, error } = await supabase
        .from('documents')
        .insert({
          client_id: clientId,
          uploaded_by_user_id: user.id,
          name: input.name.trim() || input.file.name,
          document_type: input.documentType,
          supplier_name: input.supplierName?.trim() || null,
          document_date: input.documentDate || null,
          notes: input.notes?.trim() || null,
          transaction_id: input.transactionId ?? null,
          file_path: path,
          mime_type: input.file.type,
          size_bytes: input.file.size,
        })
        .select()
        .single();

      if (error) {
        await supabase.storage.from('documents').remove([path]);
        throw error;
      }
      return data as ClinanceDocument;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Documento enviado.');
    },
    onError: (e: Error) => toast.error(`Erro no upload: ${e.message}`),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();

  // Soft delete: mantém o blob no storage por enquanto. Purge físico futuro
  // (cron) lê documents.deleted_at < now() - 90 days e remove storage + row.
  return useMutation({
    mutationFn: async (doc: ClinanceDocument) => {
      const { error } = await supabase
        .from('documents')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Documento excluído.');
    },
    onError: (e: Error) => toast.error(`Erro ao excluir: ${e.message}`),
  });
}

export async function getDocumentSignedUrl(filePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('documents')
    .createSignedUrl(filePath, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}
